import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SAR_IDENTIFIER_MAX, sarCreateSchema } from '@today/models'
import {
	COMPLIANCE_CONTEXTS,
	type SarFormat,
	SarIdentifierType,
	SarStatus,
	type SubjectAccessRequestDTO,
} from '@today/types'
import type {
	SarFormValues,
	SarRowView,
	SubjectAccessFormViewProps,
	SubjectAccessListViewProps,
	Translate,
} from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { complianceCapabilities } from '@/auth/compliance-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { COMPLIANCE_NAMESPACE } from '@/i18n'
import { complianceService } from '@/services/compliance.service'
import { complianceError } from './compliance-errors'
import { formatDate, listParams, openDownload, POLLED_SAR_STATUS, REFRESH_MS, statusOptions } from './compliance-ui'

const SARS_KEY = ['compliance', 'sars'] as const
const EMPTY: SarFormValues = { identifierType: SarIdentifierType.EMAIL, identifierValue: '' }
type Errors = Partial<Record<keyof SarFormValues, string>>

/** A READY bundle may be downloaded until its window ends (REQ-205). */
const downloadable = (sar: SubjectAccessRequestDTO, now: number) =>
	sar.status === SarStatus.READY && sar.expiresAt !== null && Date.parse(sar.expiresAt) > now

const toRow = (sar: SubjectAccessRequestDTO, t: Translate, language: string, now: number): SarRowView => ({
	id: sar.id,
	customerId: sar.customerId,
	identifierTypeLabel: t(`identifierType.${sar.identifierType}`),
	statusLabel:
		sar.status === SarStatus.COLLECTING
			? t('sars.collecting', { n: sar.sections.length, m: COMPLIANCE_CONTEXTS.length })
			: t(`sarStatus.${sar.status}`),
	requestedAt: formatDate(sar.requestedAt, language),
	dueBy: formatDate(sar.dueBy, language),
	windowEndsAt: formatDate(sar.expiresAt, language, true),
	downloadable: downloadable(sar, now),
})

const validate = (values: SarFormValues, t: (key: string) => string): Errors => {
	const value = values.identifierValue.trim()
	if (!value || value.length > SAR_IDENTIFIER_MAX) return { identifierValue: t('validation.identifierValue') }
	// The shared schema's one cross-field rule: a CUSTOMER_ID identifier must be a UUID.
	if (!sarCreateSchema.safeParse({ identifierType: values.identifierType, identifierValue: value }).success) {
		return { identifierValue: t('validation.customerId') }
	}
	return {}
}

/**
 * The SAR list (REQ-205): 20 per page with a status filter, refreshed every 5 s
 * while any SAR on the page is collecting; downloads only with `compliance.sar.manage`.
 */
export function useSubjectAccessList(): SubjectAccessListViewProps {
	const { t, i18n } = useTranslation(COMPLIANCE_NAMESPACE)
	const caps = complianceCapabilities(useViewerPermissions().permissions)
	const [status, setStatus] = useState('')
	const [page, setPage] = useState(1)
	const [message, setMessage] = useState<string | null>(null)
	const params = listParams(page, status)
	const sars = useQuery({
		queryKey: [...SARS_KEY, params],
		queryFn: () => complianceService.listSars(params),
		placeholderData: keepPreviousData,
		refetchInterval: (query) =>
			query.state.data?.data.some((sar) => sar.status === POLLED_SAR_STATUS) ? REFRESH_MS : false,
	})
	const download = useMutation({
		mutationFn: ({ id, format }: { id: string; format: SarFormat }) => complianceService.sarDownloadLink(id, format),
		onSuccess: (link) => openDownload(link.url),
		onError: (error) => setMessage(complianceError(error, t).message),
	})
	const now = Date.now()
	return {
		t,
		rows: (sars.data?.data ?? []).map((sar) => toRow(sar, t, i18n.language, now)),
		statusFilter: status,
		statusOptions: statusOptions(Object.values(SarStatus), (s) => t(`sarStatus.${s}`), t('common.all')),
		page,
		pageCount: sars.data?.totalPages ?? 0,
		error: sars.isError,
		message,
		onStatusChange: (next) => {
			setStatus(next)
			setPage(1)
		},
		onPageChange: setPage,
		onRetry: () => void sars.refetch(),
		onDownload: caps.manageSars
			? (id, format) => {
					setMessage(null)
					download.mutate({ id, format })
				}
			: undefined,
	}
}

/**
 * The "New access request" form (REQ-205). A `SUBJECT_AMBIGUOUS` refusal lists
 * the candidates; choosing one resubmits by `CUSTOMER_ID`. The typed value is
 * cleared once a request is created.
 */
export function useSubjectAccessForm(): SubjectAccessFormViewProps {
	const { t } = useTranslation(COMPLIANCE_NAMESPACE)
	const queryClient = useQueryClient()
	const [values, setValues] = useState(EMPTY)
	const [errors, setErrors] = useState<Errors>({})
	const [message, setMessage] = useState<string | null>(null)
	const [candidates, setCandidates] = useState<string[]>([])
	const create = useMutation({
		mutationFn: complianceService.createSar,
		onSuccess: () => {
			setValues(EMPTY)
			void queryClient.invalidateQueries({ queryKey: SARS_KEY })
		},
		onError: (error) => {
			const view = complianceError(error, t)
			setMessage(view.message)
			setErrors(view.fields)
			setCandidates(view.candidates)
		},
	})
	const submit = (identifierType: SarIdentifierType, identifierValue: string) => {
		setMessage(null)
		setCandidates([])
		create.mutate({ identifierType, identifierValue })
	}
	return {
		t,
		values,
		errors,
		message,
		submitting: create.isPending,
		identifierTypes: Object.values(SarIdentifierType).map((type) => ({ id: type, label: t(`identifierType.${type}`) })),
		candidates,
		onChange: (field, value) => setValues((current) => ({ ...current, [field]: value })),
		onSubmit: () => {
			const found = validate(values, t)
			setErrors(found)
			if (Object.keys(found).length === 0) {
				submit(values.identifierType as SarIdentifierType, values.identifierValue.trim())
			}
		},
		onChooseCandidate: (customerId) => submit(SarIdentifierType.CUSTOMER_ID, customerId),
	}
}
